import {useMemo} from 'react';
import type {KeyboardEvent} from 'react';
import cx from 'classnames';

import UserAvatar from '@/svg/user-avatar';
import Img from '@/images';
import styles from './avatar.module.scss';

export const AVATAR_VARIANTS = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY'
} as const;

export const AVATAR_SIZES = {
  SMALL: 'SMALL',
  LARGE: 'LARGE'
} as const;

export type AvatarVariant = (typeof AVATAR_VARIANTS)[keyof typeof AVATAR_VARIANTS];
export type AvatarSize = (typeof AVATAR_SIZES)[keyof typeof AVATAR_SIZES];

export interface AvatarUser {
  avatarUrl?: string | null;
  nameInitials?: string | null;
  fullName?: string;
}

interface AvatarProps {
  user?: AvatarUser | null;
  size?: AvatarSize;
  variant?: AvatarVariant;
  isActive?: boolean;
  hasPointerOnHover?: boolean;
  onClickCb?: () => void;
  disableStates?: boolean;
  isDropdownOpen?: boolean;
}

export const AVATAR_USER_NOT_FOUND_ERROR = '[Avatar]: No user found!';

const Avatar = ({
  user = null,
  size = AVATAR_SIZES.SMALL,
  variant = AVATAR_VARIANTS.PRIMARY,
  isActive = false,
  hasPointerOnHover = false,
  disableStates = false,
  isDropdownOpen = false,
  onClickCb = () => {}
}: AvatarProps) => {
  if (!user) throw new Error(AVATAR_USER_NOT_FOUND_ERROR);

  const isSmall = useMemo(() => size === AVATAR_SIZES.SMALL, [size]);
  const isPrimaryVariant = useMemo(() => variant === AVATAR_VARIANTS.PRIMARY, [variant]);
  const hasAvatar = useMemo(() => Boolean(user.avatarUrl && user.avatarUrl.length), [user.avatarUrl]);
  const hasInitials = useMemo(
    () => Boolean(user.nameInitials && user.nameInitials.length),
    [user.nameInitials]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onClickCb();
    }
  };

  const isAvatarActive = useMemo(
    () => !disableStates && (isActive || isDropdownOpen),
    [disableStates, isActive, isDropdownOpen]
  );
  const hasHoverPointer = useMemo(
    () => !disableStates && hasPointerOnHover,
    [disableStates, hasPointerOnHover]
  );

  const component = useMemo(() => {
    if (hasAvatar) return <Img src={user.avatarUrl ?? ''} alt={user.fullName ?? 'User avatar'} />;

    if (hasInitials) {
      return (
        <div className={styles['avatar-initials']}>
          <span>{user.nameInitials}</span>
        </div>
      );
    }

    return <UserAvatar width="100%" height="100%" />;
  }, [hasAvatar, hasInitials, user.avatarUrl, user.fullName, user.nameInitials]);

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="ui-avatar"
      className={cx(styles.avatar, {
        [styles['avatar-lg']]: !isSmall,
        [styles['avatar-neutral50']]: !isPrimaryVariant,
        [styles['avatar-active']]: isAvatarActive,
        [styles['avatar-hover']]: hasHoverPointer
      })}
      onClick={onClickCb}
      onKeyDown={handleKeyDown}>
      {component}
    </div>
  );
};

export default Avatar;
